/**
 * ProductsListPage — listagem moderna do produtor (V2).
 *
 * Substitui MyProductsPage (V1 antigo).
 * Consome /api/v2/products/me via productsV2API.
 *
 * Layout: header com CTA, 4 stats cards, busca + filtros, grid responsivo de
 * cards com cover, badges, categoria pill, preço e ações inline.
 *
 * Design system: padronizado em variáveis CSS (--plist-*), dark mode ready,
 * sem dependência de Bootstrap.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import MasterLayout from '../../masterLayout/MasterLayout';
import { productsV2API } from '../../services/api/v2/products';
import { plansV2API } from '../../services/api/v2/plans';
import { mapProductV2ToV1 } from '../../utils/v2ShapeMapper';
import './ProductsListPage.css';

// ─── Constantes ──────────────────────────────────────────────────────────

const APPROVAL = {
  approved: { label: 'Aprovado', cls: 'plist__badge--approved', icon: 'mdi:check-decagram' },
  pending_approval: { label: 'Em análise', cls: 'plist__badge--pending', icon: 'mdi:clock-outline' },
  rejected: { label: 'Reprovado', cls: 'plist__badge--rejected', icon: 'mdi:close-circle-outline' },
};

const STATUS = {
  active: { label: 'Ativo', cls: 'plist__badge--active' },
  paused: { label: 'Pausado', cls: 'plist__badge--paused' },
  archived: { label: 'Arquivado', cls: 'plist__badge--archived' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────

// fmtBRL recebe CENTAVOS e divide por 100 internamente
const fmtBRL = (cents) =>
  (Number(cents || 0) / 100).toLocaleString('pt-BR', {
    style: 'currency', currency: 'BRL',
  });

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
    });
  } catch { return '—'; }
};

/**
 * Resolve preço de exibição a partir dos planos:
 * - Sem planos: "—"
 * - 1 plano: R$ X
 * - 2+ planos: "de R$ X a R$ Y"
 */
const resolvePriceFromPlans = (plans = []) => {
  if (!plans.length) return null;
  const prices = plans
    .map((p) => Number(p.price_cents || p.price || 0))
    .filter((v) => v > 0);
  if (!prices.length) return null;
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  // Mantém em CENTAVOS — fmtBRL converte na exibição
  return { min, max, isRange: min !== max };
};

// ─── Skeleton ────────────────────────────────────────────────────────────

const SkeletonCard = () => (
  <div className="plist__skeleton">
    <div className="plist__skeleton-cover" />
    <div className="plist__skeleton-body">
      <div className="plist__skeleton-line plist__skeleton-line--sm" />
      <div className="plist__skeleton-line plist__skeleton-line--lg" />
      <div className="plist__skeleton-line plist__skeleton-line--md" />
      <div className="plist__skeleton-line plist__skeleton-line--sm" />
    </div>
  </div>
);

// ─── Card ────────────────────────────────────────────────────────────────

const ProductCard = ({ product, plans, onView, onEdit, onDelete }) => {
  const approval = APPROVAL[product.approval_status] || APPROVAL.pending_approval;
  const status = STATUS[product.status] || STATUS.active;
  const price = resolvePriceFromPlans(plans);
  const commission = product.commission_value_affiliate
    ? `${product.commission_value_affiliate}%`
    : null;

  const cover = product.image_url || product.image;

  return (
    <article className="plist__card" onClick={() => onView(product)}>
      <div className="plist__cover">
        {cover ? (
          <img src={cover} alt={product.name} loading="lazy" />
        ) : (
          <div className="plist__cover-placeholder">
            <Icon icon="mdi:package-variant-closed" width="42" />
          </div>
        )}
        <div className="plist__cover-overlay" />

        <div className="plist__badges-top">
          <span className={`plist__badge ${approval.cls}`}>
            <Icon icon={approval.icon} width="12" />
            {approval.label}
          </span>
          <span className={`plist__badge ${status.cls}`}>{status.label}</span>
        </div>

        {product.category && (
          <span className="plist__category-pill">
            <Icon icon="mdi:folder-outline" width="11" />
            {product.category}
          </span>
        )}
      </div>

      <div className="plist__card-body">
        <span className="plist__card-title" title={product.name}>
          {product.name || 'Sem nome'}
        </span>

        <div className="plist__card-meta">
          {product.uuid && (
            <span className="plist__card-meta-item">
              <Icon icon="mdi:identifier" width="13" />
              {String(product.uuid).slice(0, 8)}…
            </span>
          )}
          {commission && (
            <span className="plist__card-meta-item">
              <Icon icon="mdi:account-tie-outline" width="13" />
              Comissão {commission}
            </span>
          )}
          <span className="plist__card-meta-item">
            <Icon icon="mdi:calendar-outline" width="13" />
            {fmtDate(product.created_at)}
          </span>
        </div>

        <div className="plist__card-price">
          {price ? (
            price.isRange ? (
              <>
                <span className="plist__card-price-value--from">de</span>
                <span className="plist__card-price-value">{fmtBRL(price.min)}</span>
                <span className="plist__card-price-value--from">até</span>
                <span className="plist__card-price-value">{fmtBRL(price.max)}</span>
              </>
            ) : (
              <span className="plist__card-price-value">{fmtBRL(price.min)}</span>
            )
          ) : (
            <span className="plist__card-price-label">Sem plano configurado</span>
          )}
        </div>

        <div className="plist__card-actions" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="plist__card-btn"
            onClick={() => onEdit(product)}
            title="Editar produto"
          >
            <Icon icon="mdi:pencil-outline" width="15" />
            Editar
          </button>
          <button
            type="button"
            className="plist__card-btn plist__card-btn--primary"
            onClick={() => onView(product)}
            title="Ver detalhes"
          >
            <Icon icon="mdi:eye-outline" width="15" />
            Ver detalhes
          </button>
          <button
            type="button"
            className="plist__card-btn plist__card-btn--danger"
            onClick={() => onDelete(product)}
            title="Excluir produto"
          >
            <Icon icon="mdi:trash-can-outline" width="15" />
            Excluir
          </button>
        </div>
      </div>
    </article>
  );
};

// ─── Página ──────────────────────────────────────────────────────────────

const ProductsListPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [plansByProduct, setPlansByProduct] = useState({}); // { [productId]: plans[] }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Carga inicial
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await productsV2API.listMine();
        const list = res.data?.products || res.data?.data || [];
        const v1Shape = list.map(mapProductV2ToV1);
        if (!alive) return;
        setProducts(v1Shape);

        // Carrega planos em paralelo (limitado pra não saturar)
        const plansResults = await Promise.allSettled(
          v1Shape.map((p) => plansV2API.listByProduct(p.id)),
        );
        if (!alive) return;
        const byProduct = {};
        plansResults.forEach((r, idx) => {
          const pid = v1Shape[idx].id;
          if (r.status === 'fulfilled') {
            byProduct[pid] = r.value.data?.plans || r.value.data?.data || [];
          } else {
            byProduct[pid] = [];
          }
        });
        setPlansByProduct(byProduct);
      } catch (err) {
        if (alive) setError(err.message || 'Erro ao carregar produtos');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // Categorias únicas (deriva da lista carregada)
  const categories = useMemo(() => {
    const set = new Set();
    products.forEach((p) => p.category && set.add(p.category));
    return Array.from(set).sort();
  }, [products]);

  // Filtros aplicados
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return products.filter((p) => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (categoryFilter !== 'all' && p.category !== categoryFilter) return false;
      if (term && !(p.name || '').toLowerCase().includes(term)) return false;
      return true;
    });
  }, [products, search, statusFilter, categoryFilter]);

  // Stats
  const stats = useMemo(() => {
    const acc = { total: products.length, active: 0, pending: 0, paused: 0 };
    products.forEach((p) => {
      if (p.status === 'active') acc.active += 1;
      if (p.status === 'paused') acc.paused += 1;
      if (p.approval_status === 'pending_approval') acc.pending += 1;
    });
    return acc;
  }, [products]);

  const handleView = (p) => {
    const path = p.uuid || p.id;
    navigate(`/products/${path}`);
  };
  const handleEdit = (p) => {
    const path = p.uuid || p.id;
    navigate(`/products/${path}/edit`);
  };
  const handleDelete = async (p) => {
    if (!window.confirm('Excluir este produto?')) return;
    try {
      await productsV2API.remove(p.id);
      // Recarrega lista removendo o item excluído do state
      setProducts((prev) => prev.filter((it) => it.id !== p.id));
      setPlansByProduct((prev) => {
        const next = { ...prev };
        delete next[p.id];
        return next;
      });
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Erro ao excluir produto';
      window.alert(msg);
    }
  };
  const handleCreate = () => navigate('/products/registration/new');

  return (
    <MasterLayout>
      <div className="products-list">
        {/* Header */}
        <div className="plist__header">
          <div className="plist__heading">
            <span>Meus Produtos</span>
            <p>Gerencie seus produtos digitais, físicos e kits em um só lugar</p>
          </div>
          <button type="button" className="plist__cta" onClick={handleCreate}>
            <Icon icon="mdi:plus" width="18" />
            Novo Produto
          </button>
        </div>

        {/* Stats */}
        <div className="plist__stats">
          <div className="plist__stat">
            <div className="plist__stat-icon plist__stat-icon--blue">
              <Icon icon="mdi:package-variant" width="22" />
            </div>
            <div className="plist__stat-info">
              <span>Total</span>
              <strong>{stats.total}</strong>
            </div>
          </div>
          <div className="plist__stat">
            <div className="plist__stat-icon plist__stat-icon--green">
              <Icon icon="mdi:check-circle" width="22" />
            </div>
            <div className="plist__stat-info">
              <span>Ativos</span>
              <strong>{stats.active}</strong>
            </div>
          </div>
          <div className="plist__stat">
            <div className="plist__stat-icon plist__stat-icon--amber">
              <Icon icon="mdi:clock-outline" width="22" />
            </div>
            <div className="plist__stat-info">
              <span>Em análise</span>
              <strong>{stats.pending}</strong>
            </div>
          </div>
          <div className="plist__stat">
            <div className="plist__stat-icon plist__stat-icon--gray">
              <Icon icon="mdi:pause-circle" width="22" />
            </div>
            <div className="plist__stat-info">
              <span>Pausados</span>
              <strong>{stats.paused}</strong>
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="plist__toolbar">
          <div className="plist__search">
            <Icon icon="mdi:magnify" className="plist__search-icon" width="18" />
            <input
              type="text"
              placeholder="Buscar pelo nome do produto..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="plist__select">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Todos os status</option>
              <option value="active">Ativos</option>
              <option value="paused">Pausados</option>
              <option value="archived">Arquivados</option>
            </select>
            <Icon icon="mdi:chevron-down" className="plist__select-icon" width="16" />
          </div>
          <div className="plist__select">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="all">Todas as categorias</option>
              {categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <Icon icon="mdi:chevron-down" className="plist__select-icon" width="16" />
          </div>
        </div>

        {/* Conteúdo */}
        {loading ? (
          <div className="plist__loading">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="plist__empty">
            <div className="plist__empty-icon" style={{ background: '#fee2e2', color: '#dc2626' }}>
              <Icon icon="mdi:alert-circle-outline" width="32" />
            </div>
            <span>Erro ao carregar produtos</span>
            <p>{error}</p>
            <button
              type="button"
              className="plist__cta"
              onClick={() => window.location.reload()}
            >
              <Icon icon="mdi:refresh" width="16" />
              Tentar novamente
            </button>
          </div>
        ) : filtered.length === 0 ? (
          <div className="plist__empty">
            <div className="plist__empty-icon">
              <Icon icon="mdi:package-variant-closed" width="32" />
            </div>
            <span>
              {products.length === 0 ? 'Nenhum produto ainda' : 'Nenhum produto encontrado'}
            </span>
            <p>
              {products.length === 0
                ? 'Cadastre seu primeiro produto pra começar a vender.'
                : 'Tente ajustar os filtros ou a busca.'}
            </p>
            {products.length === 0 && (
              <button type="button" className="plist__cta" onClick={handleCreate}>
                <Icon icon="mdi:plus" width="16" />
                Cadastrar primeiro produto
              </button>
            )}
          </div>
        ) : (
          <div className="plist__grid">
            {filtered.map((p) => (
              <ProductCard
                key={p.id}
                product={p}
                plans={plansByProduct[p.id] || []}
                onView={handleView}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </MasterLayout>
  );
};

export default ProductsListPage;
